import {
  shapeBoundingBox,
  type DesignConnectorEndpoint,
  type DesignShape,
} from '../../types/design'

export type DesignPoint = { x: number; y: number }
export type DesignBox = { x: number; y: number; width: number; height: number }

export const cloneDesignShape = (shape: DesignShape): DesignShape => {
  if (typeof structuredClone === 'function') return structuredClone(shape) as DesignShape
  return JSON.parse(JSON.stringify(shape)) as DesignShape
}

const translatePoint = (point: DesignPoint, dx: number, dy: number): DesignPoint => ({
  x: point.x + dx,
  y: point.y + dy,
})

const scalePoint = (point: DesignPoint, start: DesignBox, next: DesignBox): DesignPoint => ({
  x: next.x + ((point.x - start.x) * next.width) / start.width,
  y: next.y + ((point.y - start.y) * next.height) / start.height,
})

const rotatePoint = (point: DesignPoint, center: DesignPoint, degrees: number): DesignPoint => {
  const radians = (degrees * Math.PI) / 180
  const cos = Math.cos(radians)
  const sin = Math.sin(radians)
  const dx = point.x - center.x
  const dy = point.y - center.y
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  }
}

const updateFreeEndpoint = (
  endpoint: DesignConnectorEndpoint,
  project: (point: DesignPoint) => DesignPoint,
): DesignConnectorEndpoint => {
  if (endpoint.kind !== 'FREE') return endpoint
  const next = project(endpoint)
  return { ...endpoint, x: next.x, y: next.y }
}

const bboxPatch = (shape: DesignShape): Pick<DesignShape, 'position' | 'size'> => {
  const box = shapeBoundingBox(shape)
  return {
    position: { x: box.x, y: box.y },
    size: { width: Math.max(1, box.width), height: Math.max(1, box.height) },
  }
}

export const translateDesignShape = (
  shape: DesignShape,
  dx: number,
  dy: number,
  snapPosition?: (point: DesignPoint) => DesignPoint,
): Partial<DesignShape> => {
  const rawPosition = translatePoint(shape.position, dx, dy)
  const position = snapPosition ? snapPosition(rawPosition) : rawPosition
  const actualDx = position.x - shape.position.x
  const actualDy = position.y - shape.position.y

  if ((shape.type === 'LINE' || shape.type === 'ARROW') && shape.path) {
    const next = {
      ...shape,
      path: {
        ...shape.path,
        p1: translatePoint(shape.path.p1, actualDx, actualDy),
        p2: translatePoint(shape.path.p2, actualDx, actualDy),
      },
    }
    return { ...bboxPatch(next), path: next.path } as Partial<DesignShape>
  }
  if (shape.type === 'CURVE') {
    const next = {
      ...shape,
      p1: translatePoint(shape.p1, actualDx, actualDy),
      p2: translatePoint(shape.p2, actualDx, actualDy),
      c1: translatePoint(shape.c1, actualDx, actualDy),
      c2: translatePoint(shape.c2, actualDx, actualDy),
    }
    return { ...bboxPatch(next), p1: next.p1, p2: next.p2, c1: next.c1, c2: next.c2 } as Partial<DesignShape>
  }
  if (shape.type === 'POLYLINE' || shape.type === 'FREEHAND') {
    const next = {
      ...shape,
      points: shape.points.map((point) => translatePoint(point, actualDx, actualDy)),
    }
    return { ...bboxPatch(next), points: next.points } as Partial<DesignShape>
  }
  if (shape.type === 'CONNECTOR') {
    return {
      position,
      from: updateFreeEndpoint(shape.from, (point) => translatePoint(point, actualDx, actualDy)),
      to: updateFreeEndpoint(shape.to, (point) => translatePoint(point, actualDx, actualDy)),
    } as Partial<DesignShape>
  }
  return { position } as Partial<DesignShape>
}

export const resizeDesignShape = (
  shape: DesignShape,
  startBox: DesignBox,
  nextBox: DesignBox,
): Partial<DesignShape> => {
  if (startBox.width <= 0 || startBox.height <= 0) {
    return translateDesignShape(shape, nextBox.x - startBox.x, nextBox.y - startBox.y)
  }

  const nextBase = {
    position: {
      x: nextBox.x + ((shape.position.x - startBox.x) * nextBox.width) / startBox.width,
      y: nextBox.y + ((shape.position.y - startBox.y) * nextBox.height) / startBox.height,
    },
    size: {
      width: Math.max(1, (shape.size.width * nextBox.width) / startBox.width),
      height: Math.max(1, (shape.size.height * nextBox.height) / startBox.height),
    },
  }

  if ((shape.type === 'LINE' || shape.type === 'ARROW') && shape.path) {
    const next = {
      ...shape,
      path: {
        ...shape.path,
        p1: scalePoint(shape.path.p1, startBox, nextBox),
        p2: scalePoint(shape.path.p2, startBox, nextBox),
      },
    }
    return { ...bboxPatch(next), path: next.path } as Partial<DesignShape>
  }
  if (shape.type === 'CURVE') {
    const next = {
      ...shape,
      p1: scalePoint(shape.p1, startBox, nextBox),
      p2: scalePoint(shape.p2, startBox, nextBox),
      c1: scalePoint(shape.c1, startBox, nextBox),
      c2: scalePoint(shape.c2, startBox, nextBox),
    }
    return { ...bboxPatch(next), p1: next.p1, p2: next.p2, c1: next.c1, c2: next.c2 } as Partial<DesignShape>
  }
  if (shape.type === 'POLYLINE' || shape.type === 'FREEHAND') {
    const next = {
      ...shape,
      points: shape.points.map((point) => scalePoint(point, startBox, nextBox)),
    }
    return { ...bboxPatch(next), points: next.points } as Partial<DesignShape>
  }
  if (shape.type === 'CONNECTOR') {
    return {
      ...nextBase,
      from: updateFreeEndpoint(shape.from, (point) => scalePoint(point, startBox, nextBox)),
      to: updateFreeEndpoint(shape.to, (point) => scalePoint(point, startBox, nextBox)),
    } as Partial<DesignShape>
  }
  return nextBase as Partial<DesignShape>
}

export const rotateDesignShape = (
  shape: DesignShape,
  center: DesignPoint,
  degrees: number,
): Partial<DesignShape> => {
  if ((shape.type === 'LINE' || shape.type === 'ARROW') && shape.path) {
    const next = {
      ...shape,
      rotation: 0,
      path: {
        ...shape.path,
        p1: rotatePoint(shape.path.p1, center, degrees),
        p2: rotatePoint(shape.path.p2, center, degrees),
      },
    }
    return { ...bboxPatch(next), rotation: 0, path: next.path } as Partial<DesignShape>
  }
  if (shape.type === 'CURVE') {
    const next = {
      ...shape,
      rotation: 0,
      p1: rotatePoint(shape.p1, center, degrees),
      p2: rotatePoint(shape.p2, center, degrees),
      c1: rotatePoint(shape.c1, center, degrees),
      c2: rotatePoint(shape.c2, center, degrees),
    }
    return { ...bboxPatch(next), rotation: 0, p1: next.p1, p2: next.p2, c1: next.c1, c2: next.c2 } as Partial<DesignShape>
  }
  if (shape.type === 'POLYLINE' || shape.type === 'FREEHAND') {
    const next = {
      ...shape,
      rotation: 0,
      points: shape.points.map((point) => rotatePoint(point, center, degrees)),
    }
    return { ...bboxPatch(next), rotation: 0, points: next.points } as Partial<DesignShape>
  }
  if (shape.type === 'CONNECTOR') {
    return {
      rotation: 0,
      from: updateFreeEndpoint(shape.from, (point) => rotatePoint(point, center, degrees)),
      to: updateFreeEndpoint(shape.to, (point) => rotatePoint(point, center, degrees)),
    } as Partial<DesignShape>
  }
  return { rotation: degrees } as Partial<DesignShape>
}
